import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { Field, Input, Spinner } from './ui.js'

/**
 * An address, found or typed.
 *
 * Search on top, the real fields underneath, both on screen at once. The two
 * halves are not modes: an autocomplete on its own cannot express "the new
 * estate off Rutherford Road that Google has not heard of", and bare fields on
 * their own are how the same house arrives spelt four ways and lands on the map
 * in the middle of the ocean. Picking a suggestion fills the fields in, and the
 * fields stay editable afterwards.
 *
 * The lookup is proxied by `places.*`, so the Google key stays on the server —
 * a `VITE_` key would be compiled into the bundle and shipped to every phone.
 * With no key configured the search box is not drawn at all and this is the
 * plain form it replaced.
 */

export type AddressDraft = RouterOutputs['places']['details']

export function emptyAddress(): AddressDraft {
  return {
    raw: null,
    unit: null,
    streetNumber: null,
    street: null,
    suburb: null,
    city: null,
    state: null,
    postcode: null,
    country: 'AU',
    latitude: null,
    longitude: null,
    placeId: null,
  }
}

/** An existing site's address, in the shape this component edits. */
export function addressDraftFrom(address: {
  raw?: string | null
  unit?: string | null
  streetNumber?: string | null
  street?: string | null
  suburb?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  country?: string | null
  latitude?: number | null
  longitude?: number | null
  placeId?: string | null
} | null | undefined): AddressDraft {
  if (!address) return emptyAddress()

  return {
    raw: address.raw ?? null,
    unit: address.unit ?? null,
    streetNumber: address.streetNumber ?? null,
    street: address.street ?? null,
    // The import put ServiceM8's locality in whichever of the two it could
    // parse, so the editor shows one Suburb box over both.
    suburb: address.suburb ?? address.city ?? null,
    city: null,
    state: address.state ?? null,
    postcode: address.postcode ?? null,
    country: address.country ?? 'AU',
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    placeId: address.placeId ?? null,
  }
}

/** Enough of an address to send somebody to it. */
export function addressIsUsable(address: AddressDraft): boolean {
  return Boolean(address.street?.trim() && address.suburb?.trim())
}

export function AddressField({
  id = 'address',
  value,
  onChange,
  autoFocus,
}: {
  id?: string
  value: AddressDraft
  onChange: (address: AddressDraft) => void
  autoFocus?: boolean
}) {
  const trpc = useTRPC()

  const [query, setQuery] = useState('')
  const [typed, setTyped] = useState('')

  // Google bills per request, so the box does not ask on every keystroke.
  // Same 250ms as the list search boxes.
  useEffect(() => {
    if (typed === query) return
    const timer = setTimeout(() => setQuery(typed), 250)
    return () => clearTimeout(timer)
  }, [typed, query])

  // One token covers a run of keystrokes and the details call that ends it, and
  // Google charges the lot as a single lookup. A fresh one per address is the
  // point — reusing it across two addresses is what makes a session unbounded.
  const [sessionToken, setSessionToken] = useState(newSessionToken)

  const status = useQuery(trpc.places.status.queryOptions())
  const configured = status.data?.configured ?? false

  const hits = useQuery({
    ...trpc.places.suggest.queryOptions({ q: query, sessionToken }),
    enabled: configured && query.trim().length >= 3,
  })

  // The details call reads, so it is a query — but it fires on a click rather
  // than on render, which is what `fetchQuery` is for.
  const queryClient = useQueryClient()
  const [resolving, setResolving] = useState(false)
  const [lookupFailed, setLookupFailed] = useState(false)

  async function pick(placeId: string) {
    setResolving(true)
    setLookupFailed(false)

    try {
      const resolved = await queryClient.fetchQuery(
        trpc.places.details.queryOptions({ placeId, sessionToken }),
      )

      onChange(resolved)
      setTyped('')
      setQuery('')
      // That lookup closed the billing session; the next address opens a new one.
      setSessionToken(newSessionToken())
    } catch {
      // The fields below are untouched and still typeable, which is the whole
      // reason they are on screen rather than behind a "enter manually" link.
      setLookupFailed(true)
    } finally {
      setResolving(false)
    }
  }

  /**
   * A field corrected by hand.
   *
   * The place id is dropped, and the coordinates are kept. Once a person has
   * changed the text, "Google's record for this place" is no longer a true
   * claim about what is in the box, and leaving the id on would point two
   * different addresses at one place and let `createSite` merge them. The point
   * survives because a corrected unit number does not move the house — it just
   * stops being an `EXACT` geocode and becomes a `MANUAL` one.
   */
  function edit(part: Partial<AddressDraft>) {
    onChange({ ...value, ...part, placeId: null })
  }

  return (
    <div className="flex flex-col gap-3">
      {configured ? (
        <Field
          label="Find the address"
          htmlFor={`${id}-search`}
          hint="Or fill the boxes below in yourself."
        >
          <Input
            id={`${id}-search`}
            autoFocus={autoFocus}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            // Mounted inside other forms, where Enter would submit the job
            // rather than run the search it looks like it runs.
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault()
            }}
            placeholder="Start typing a street address…"
          />
          <Suggestions
            query={query}
            loading={hits.isFetching}
            hits={hits.data ?? []}
            resolving={resolving}
            onPick={(placeId) => void pick(placeId)}
          />
          {lookupFailed ? (
            <p className="text-xs text-amber-800" role="alert">
              That address would not come back from Google. The boxes below still work.
            </p>
          ) : null}
        </Field>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-[90px_110px_1fr]">
        <Field label="Unit" htmlFor={`${id}-unit`}>
          <Input
            id={`${id}-unit`}
            value={value.unit ?? ''}
            onChange={(event) => edit({ unit: event.target.value || null })}
          />
        </Field>
        <Field label="Number" htmlFor={`${id}-street-number`}>
          <Input
            id={`${id}-street-number`}
            autoFocus={autoFocus && !configured}
            value={value.streetNumber ?? ''}
            onChange={(event) => edit({ streetNumber: event.target.value || null })}
          />
        </Field>
        <Field label="Street" htmlFor={`${id}-street`}>
          <Input
            id={`${id}-street`}
            value={value.street ?? ''}
            onChange={(event) => edit({ street: event.target.value || null })}
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_90px_110px]">
        <Field label="Suburb" htmlFor={`${id}-suburb`}>
          <Input
            id={`${id}-suburb`}
            value={value.suburb ?? ''}
            onChange={(event) => edit({ suburb: event.target.value || null })}
          />
        </Field>
        <Field label="State" htmlFor={`${id}-state`}>
          <Input
            id={`${id}-state`}
            value={value.state ?? ''}
            onChange={(event) => edit({ state: event.target.value || null })}
          />
        </Field>
        <Field label="Postcode" htmlFor={`${id}-postcode`}>
          <Input
            id={`${id}-postcode`}
            inputMode="numeric"
            value={value.postcode ?? ''}
            onChange={(event) => edit({ postcode: event.target.value || null })}
          />
        </Field>
      </div>

      <MapPinNote address={value} />
    </div>
  )
}

/**
 * Whether this address can be put on the board.
 *
 * Worth saying out loud rather than leaving to be discovered: without
 * coordinates the property is missing from the map view, `nearestVan` cannot
 * measure it, and the travel stripe on a booking has nothing to estimate from.
 * That is a real consequence of typing an address rather than picking one, and
 * it is invisible on the form that causes it.
 */
function MapPinNote({ address }: { address: AddressDraft }) {
  const located = address.latitude != null && address.longitude != null

  if (located) {
    return (
      <p className="text-xs text-ink-500">
        {address.placeId
          ? 'Located. This one lands on the map and can be measured for travel.'
          : 'Has a map pin from before, kept through your correction.'}
      </p>
    )
  }

  return (
    <p className="text-xs text-amber-800">
      No map pin. The job is saved either way, but it will not appear on the map view and no
      travel time can be estimated to it.
    </p>
  )
}

function Suggestions({
  query,
  loading,
  hits,
  resolving,
  onPick,
}: {
  query: string
  loading: boolean
  hits: RouterOutputs['places']['suggest']
  resolving: boolean
  onPick: (placeId: string) => void
}) {
  if (query.trim().length < 3) return null

  if (resolving) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-ink-400">
        <Spinner className="size-3" /> Getting the details…
      </p>
    )
  }

  if (loading && hits.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-ink-400">
        <Spinner className="size-3" /> Looking…
      </p>
    )
  }

  if (hits.length === 0) {
    return <p className="text-xs text-ink-500">No address by that name. Type it in below.</p>
  }

  return (
    <ul className="max-h-52 overflow-y-auto rounded-md ring-1 ring-inset ring-ink-200">
      {hits.map((hit) => (
        <li key={hit.placeId}>
          <button
            type="button"
            onClick={() => onPick(hit.placeId)}
            className="flex min-h-[var(--control-sm)] w-full flex-col justify-center px-2.5 py-1.5 text-left transition-colors hover:bg-ink-50"
          >
            <span className="truncate text-[13px] font-medium text-ink-900">{hit.primary}</span>
            {hit.secondary ? (
              <span className="truncate text-[11.5px] text-ink-500">{hit.secondary}</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * A token Google recognises as "one address being looked up".
 *
 * `randomUUID` is unavailable on an insecure origin, which is exactly the
 * configuration a technician's handset is on when the office serves the app
 * over plain HTTP on the local network. The fallback only has to be unique
 * enough to group one person's keystrokes.
 */
function newSessionToken(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
}
